import {
  ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, NgZone, OnInit, Output, ViewChild,
  ChangeDetectorRef, ContentChild, AfterContentInit, OnDestroy
} from '@angular/core';
import { Router } from '@angular/router';
import { fadeWidthInOutAnimation } from '../_services/layout.animations';
import { CtrlNavigationContext, CtrlNavigationEntry } from '../../shared/_models/models';
import { SpearadAppService } from '../../_services';
import { AnimationItem } from 'lottie-web';
import { AnimationOptions } from 'ngx-lottie';
import { Common } from '../../shared/_common/common.helper';
import { LayoutService } from '../_services/layout.service';
import { LottieCacheService } from '../../shared/_services/lottie-cache.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-unruly-navigation-item',
  templateUrl: './unruly-navigation-item.component.html',
  styleUrls: ['./unruly-navigation-item.component.less'],
  animations: [fadeWidthInOutAnimation],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UnrulyNavigationItemComponent implements OnInit, AfterContentInit, OnDestroy {

  @ViewChild('navItemWrapper') navItemWrapper: ElementRef;
  @ViewChild('navItemChildren') navItemChildren: ElementRef;
  @ContentChild('projectedIcon', { static: false }) projectedIcon?: ElementRef;

  @Input() context: CtrlNavigationContext;
  @Input() parent: CtrlNavigationEntry;
  @Output() clicked: EventEmitter<{ actionId: string; payload?: { context?: CtrlNavigationContext } }> = new EventEmitter();
  _isCollapsed = false;

  options: AnimationOptions = {
    path: null,
    autoplay: false,
    loop: 0
  };

  hasProjectedIcon = false;
  fixedLocationPosition: Partial<CSSStyleDeclaration> = null;

  private animationItem: AnimationItem;
  private unsubscribe$ = new Subject<void>();

  constructor(
    private router: Router,
    private spearadService: SpearadAppService,
    private ngZone: NgZone,
    private common: Common,
    private layoutService: LayoutService,
    private cdRef: ChangeDetectorRef,
    private lottieCache: LottieCacheService
  ) {
  }

  get isCollapsed(): boolean {
    return this._isCollapsed;
  }

  @Input()
  set isCollapsed(isCollapsed: boolean) {
    if (isCollapsed !== this.isCollapsed) {
      this.fixedLocationPosition = null;
    }
    this._isCollapsed = isCollapsed;
  }

  ngOnInit() {
    if (this.parent?.lottieFile) {
      this.lottieCache.getAnimation(`resources/20250805075334/assets/lottie/${this.parent.lottieFile}.json`)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe({
          next: (animationData) => {
            this.options = {
              animationData: animationData,
              autoplay: false,
              loop: 0
            };
            this.cdRef.detectChanges();
          },
          error: (error) => {
            // Fallback to path-based loading if caching fails
            console.warn('Failed to load cached animation, falling back to path:', error);
            this.options['path'] = `resources/20250805075334/assets/lottie/${this.parent.lottieFile}.json`;
          }
        });
    }
  }

  ngAfterContentInit() {
    this.hasProjectedIcon = !!this.projectedIcon;
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  onMouseEnter() {
    this.playLottie();
    if (this.isCollapsed) {
      this.fixedLocationPosition = { position: 'fixed', top: '-9999px', left: '-9999px', zIndex: '1001' };
      this.cdRef.detectChanges();

      if (this.navItemWrapper?.nativeElement && this.navItemChildren?.nativeElement) {
        setTimeout(() => {
          const style = this.layoutService.calculateFixedElementPositionSync(
            this.navItemWrapper?.nativeElement,
            this.navItemChildren?.nativeElement,
            'right',
            0
          );
          this.fixedLocationPosition &&= { ...style, zIndex: '1001' };
          this.cdRef.detectChanges();
        }, 0);
      } else {
        this.fixedLocationPosition = null;
        this.cdRef.detectChanges();
      }
    }
  }

  onMouseLeave() {
    this.stopLottie();
    if (this.isCollapsed) {
      this.fixedLocationPosition = null;
      this.cdRef.detectChanges();
    }
  }

  itemClicked(item: CtrlNavigationEntry) {
    if (item.children?.length) {
      this.parent.showChildren = !this.parent.showChildren;
    } else if (item.path) {
      this.navigateTo(item);
    } else if (item.actionId) {
      this.clicked.emit({ actionId: item.actionId, payload: item.actionPayload || null });
    }
  }

  navigateTo(item: CtrlNavigationEntry) {
    if (item.path === null) {
      return;
    }
    if (item.iframe) {
      this.spearadService.navigate(item.path.join('/'));
      this.router.navigate(['spearad', ...item.path]);
    } else {
      this.router.navigate(item.path);
    }
  }

  getRouterLinkPath(item: CtrlNavigationEntry): string[] {
    if (item.actionId) {
      return [location.pathname];
    } else if (item.path) {
      return [`${item.iframe ? '/spearad' : ''}/${item.path?.join('/')}`];
    }
    return null;
  }


  getRouterLinkQueryParams(item: CtrlNavigationEntry): { [key: string]: string } {
    if (item.path) {
      return {};
    }
    return this.common.toQueryParamsObject(location.search);
  }


  // isChildVisible(child: CtrlNavigationEntry) {
  //   return this.layoutService.isNavItemVisible(child);
  // }


  animationCreated(animationItem: AnimationItem): void {
    if (!this.parent.lottieFile) {
      return;
    }
    this.animationItem = animationItem;
    this.stopLottie();
  }


  playLottie(): void {
    if (this.animationItem && !this.parent.isSelected) {
      this.ngZone.runOutsideAngular(() => {
        setTimeout(() => {
          this.animationItem?.stop();
          this.animationItem?.play();
        }, 0);
      });
    }
  }


  stopLottie(): void {
    if (this.animationItem && !this.parent.isSelected) {
      this.ngZone.runOutsideAngular(() => {
        setTimeout(() => {
          this.animationItem.stop();
        }, 0);
      });
    }
  }
}
